import { createElement, type ReactNode } from "react";

import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import type { SummaryDetailItemData } from "@/admin/components/shared/summary-detail-card";
import type {
  EntityAttributeDefinition,
  EntityTableDisplayColumn,
  EntityTypeDefinition,
} from "@/admin/hooks/use-entities";
import {
  displayColumnLabel,
  isWorkflowStateResolution,
  resolveRegistrationDisplayColumnValue,
} from "@/admin/lib/entity-card-display";
import { isMoneyObject } from "@/admin/lib/formatting";
import type { Holding } from "@/admin/types/products";
import {
  defineField,
  FILE_FIELD_TYPES,
  formatDisplayValue,
  isDisplayEmpty,
  isFieldVisible,
  type FieldType,
  type ValidationRule,
} from "@/shared/lib/forms";
import { WorkflowStateBadge } from "@/shared/components/workflows";

export interface RegistrationConfiguredFieldItem {
  key: string;
  label: string;
  value: unknown;
  displayValue: ReactNode;
  muted: boolean;
}

interface RegistrationConfiguredFieldOptions {
  holding: Holding | undefined | null;
  columns: EntityTableDisplayColumn[];
  entityTypes: EntityTypeDefinition[];
  statusWorkflowKey?: string;
  locale?: string;
  currency?: string;
}

function isEmptyDisplayValue(value: unknown) {
  // A resolved workflow-state object is never "empty" (it always carries a
  // label). Workflow columns with no matching instance resolve to "" instead,
  // which the null/undefined/"" check below already treats as empty.
  if (isWorkflowStateResolution(value)) return !value.label;
  return value === null || value === undefined || value === "";
}

function shouldUseDetailCardFormatter(value: unknown) {
  return Boolean(value) && typeof value === "object" && !isMoneyObject(value);
}

export function buildRegistrationConfiguredFieldItems({
  holding,
  columns,
  entityTypes,
  statusWorkflowKey,
  locale,
  currency,
}: RegistrationConfiguredFieldOptions): RegistrationConfiguredFieldItem[] {
  return columns.map((column) => {
    const value = resolveRegistrationDisplayColumnValue(holding, column, {
      statusWorkflowKey,
      entityTypes,
    });

    const attribute = entityTypes
      .find((e) => e.key === column.entity_key)
      ?.attributes.find((a) => a.code_name === column.attribute_code_name);

    const field = defineField({
      code_name: column.id,
      display_name: displayColumnLabel(column),
      field_type: (attribute?.field_type ?? "TEXT") as FieldType,
      configuration_data: attribute?.configuration_data,
    });

    const fieldType = attribute?.field_type ?? "TEXT";
    // Workflow-state columns (Payment / Status) render a WorkflowStateBadge
    // instead of the plain formatter - the resolved value is a tagged object
    // (see resolveWorkflowStateColumnValue), which formatDisplayValue can't
    // render.
    const displayValue = isWorkflowStateResolution(value)
      ? createElement(WorkflowStateBadge, {
          workflowKey: value.workflowKey,
          stateKey: value.stateKey,
          label: value.label,
          color: value.color,
          isTerminal: value.isTerminal,
        })
      : shouldUseDetailCardFormatter(value) &&
          fieldType !== "ADDRESS" &&
          // A file arrives as a {display, value} envelope; the file formatter
          // renders it as the file's name linking to the file.
          !FILE_FIELD_TYPES.has(fieldType)
        ? undefined
        : formatDisplayValue(field, value, { locale, currency });

    return {
      key: column.id,
      label: displayColumnLabel(column),
      value,
      displayValue,
      muted: isEmptyDisplayValue(value),
    };
  });
}

export function buildRegistrationConfiguredCardItems(
  options: RegistrationConfiguredFieldOptions,
): CardFieldItem[] {
  // Hide configured card rows whose value is empty, so a card shows only the
  // fields actually present on the record (e.g. SKU / Model only "where
  // available"). Tables keep all columns; this applies to cards only.
  return buildRegistrationConfiguredFieldItems(options)
    .filter((item) => !isEmptyDisplayValue(item.value))
    .map((item) => {
    const column = options.columns.find((c) => c.id === item.key);
    const attribute = column
      ? options.entityTypes
          .find((e) => e.key === column.entity_key)
          ?.attributes.find((a) => a.code_name === column.attribute_code_name)
      : undefined;
    return {
      field: defineField({
        code_name: item.key,
        display_name: item.label,
        field_type: (attribute?.field_type ?? "TEXT") as FieldType,
        configuration_data: attribute?.configuration_data,
      }),
      value: item.value,
      displayValue: item.displayValue,
      readOnly: true,
    };
  });
}

/**
 * The HOLDING attribute a registration-card column edits, or `null` when the
 * column is display-only.
 *
 * Only a value captured on THIS registration is editable: an EAV attribute a
 * product template projects onto the `holding` entity at HOLDING level
 * (`configuration_data.value_source === "product_attribute"`). Relationship
 * columns (Product / Brand), computed values, transaction captures and other
 * entities' columns stay read-only, and an attribute the registry marks
 * `editable: false` is never offered. The save path (`PUT /registrations/<uuid>/`)
 * independently rejects any code that is not on the holding's own template.
 */
export function editableHoldingCardAttribute(
  column: EntityTableDisplayColumn | undefined,
  entityTypes: EntityTypeDefinition[],
): EntityAttributeDefinition | null {
  if (!column || column.entity_key !== "holding") return null;
  if (column.relationship_key || column.relationship_path?.length) return null;
  const attr = entityTypes
    .find((e) => e.key === "holding")
    ?.attributes?.find((a) => a.code_name === column.attribute_code_name);
  if (!attr || attr.source !== "EAV" || attr.editable === false) return null;
  const cd = attr.configuration_data;
  if (cd?.value_source !== "product_attribute") return null;
  if (cd.attribute_level && cd.attribute_level !== "HOLDING") return null;
  return attr;
}

/**
 * Card items for a config-driven holding card (the registration Product card):
 * every configured row is displayed as built by
 * `buildRegistrationConfiguredFieldItems`, and the rows backed by a
 * registration-level attribute (see `editableHoldingCardAttribute`) become
 * editable fields keyed by their attribute `code_name`, so the edit form posts
 * straight to the holding's `attribute_values`. Field type, options, required
 * and validation rules all come from the attribute's registry metadata.
 */
export function buildHoldingCardFieldItems({
  holding,
  rows,
  columns,
  entityTypes,
}: {
  holding: Holding | undefined | null;
  rows: RegistrationConfiguredFieldItem[];
  columns: EntityTableDisplayColumn[];
  entityTypes: EntityTypeDefinition[];
}): CardFieldItem[] {
  const seen = new Set<string>();
  return rows.map((row) => {
    const column = columns.find((c) => c.id === row.key);
    const attr = editableHoldingCardAttribute(column, entityTypes);
    // An empty row falls back to the card's own formatter so it renders muted.
    const displayValue = row.muted ? undefined : row.displayValue;
    if (!attr || seen.has(attr.code_name)) {
      return {
        field: defineField({
          code_name: row.key,
          display_name: row.label,
          field_type: "TEXT",
        }),
        value: row.value,
        displayValue,
        readOnly: true,
      };
    }
    seen.add(attr.code_name);
    const cd = attr.configuration_data;
    // Edit the stored value, not the display fallback (`default_value`).
    const stored = holding?.attribute_values?.find(
      (av) => av.code_name === attr.code_name,
    )?.value;
    return {
      field: defineField({
        code_name: attr.code_name,
        display_name: row.label,
        field_type: attr.field_type as FieldType,
        required: attr.required,
        hint: attr.hint || undefined,
        configuration_data: cd,
        options: cd?.options,
        validation_rules: Array.isArray(cd?.validation_rules)
          ? (cd.validation_rules as ValidationRule[])
          : undefined,
      }),
      value: row.value,
      editValue: stored ?? "",
      displayValue,
    };
  });
}

/**
 * The subset of `values` that differs from each editable item's starting
 * value, so an edit saves and audits only what the user actually changed.
 */
export function changedCardValues(
  items: CardFieldItem[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const item of items) {
    if (item.readOnly) continue;
    const code = item.field.code_name;
    if (!(code in values)) continue;
    const before = item.editValue ?? item.value ?? "";
    const after = values[code] ?? "";
    if (isDisplayEmpty(before) && isDisplayEmpty(after)) continue;
    if (JSON.stringify(before) !== JSON.stringify(after)) changed[code] = after;
  }
  return changed;
}

export function buildRegistrationConfiguredSummaryItems(
  options: RegistrationConfiguredFieldOptions,
): SummaryDetailItemData[] {
  return buildRegistrationConfiguredFieldItems(options).map((item) => ({
    key: item.key,
    label: item.label,
    value: item.displayValue,
    muted: item.muted,
  }));
}

export interface GroupedHoldingCard {
  /** The `registration_group` name, used as the card title. */
  title: string;
  items: CardFieldItem[];
}

interface GroupedHoldingCardOptions {
  holding: Holding | undefined | null;
  holdingEntity: EntityTypeDefinition | undefined | null;
}

/**
 * Whether a template-projected grouped HOLDING attribute belongs on this
 * holding, given the holding's own product template uuid.
 *
 * Only attributes projected from a product template (`value_source ===
 * "product_attribute"`) are template-scoped: they carry `source_owner_uuid` =
 * the owning product template's uuid. When both that owner uuid and the
 * holding's template uuid are known and differ, the attribute belongs to a
 * different template and is dropped. Attributes from any other source (e.g.
 * transaction-template attributes, whose `source_owner_uuid` is a transaction
 * template and would never match), attributes with no owner, and the case
 * where the holding's template is unknown are all left applicable.
 */
export function attributeAppliesToHoldingTemplate(
  attr: EntityAttributeDefinition,
  holdingTemplateUuid: string | undefined,
): boolean {
  const cd = attr.configuration_data;
  if (cd?.value_source !== "product_attribute") return true;
  const ownerUuid = cd?.source_owner_uuid;
  if (typeof ownerUuid !== "string" || !ownerUuid) return true;
  if (!holdingTemplateUuid) return true;
  return ownerUuid === holdingTemplateUuid;
}

/**
 * Whether a `holding.registration_card` display COLUMN belongs on this holding,
 * given the holding it renders. Mirrors `attributeAppliesToHoldingTemplate` for
 * the config-driven card surfaces (e.g. the claim detail Product card, which
 * maps every configured holding column to a row without an empty-value filter):
 * a HOLDING attribute column projected from a product template only belongs on
 * holdings using that template. Relationship columns and columns for other
 * entities are never template-scoped. Keeps a roofing-only capture (e.g. the
 * installed-component questions) off a Loosefill Insulation holding's card.
 */
export function holdingRegistrationCardColumnApplies(
  column: EntityTableDisplayColumn,
  holding: Holding | undefined | null,
  entityTypes: EntityTypeDefinition[],
): boolean {
  if (column.entity_key !== "holding") return true;
  if (column.relationship_key || column.relationship_path?.length) return true;
  const attr = entityTypes
    .find((e) => e.key === "holding")
    ?.attributes?.find((a) => a.code_name === column.attribute_code_name);
  if (!attr) return true;
  return attributeAppliesToHoldingTemplate(
    attr,
    holding?.product?.product_template?.uuid,
  );
}

/**
 * The set of HOLDING attribute code names that belong to a
 * `configuration_data.registration_group`. These render in their own grouped
 * card (see `buildGroupedHoldingCards`) and so are excluded from the generic
 * "Additional Details" card to avoid double-rendering. This intentionally
 * spans every template's grouped codes (not just the holding's own template)
 * so a stray/legacy grouped value never leaks into Additional Details on a
 * holding whose template the grouped card omits.
 */
export function groupedHoldingCodeNames(
  holdingEntity: EntityTypeDefinition | undefined | null,
): Set<string> {
  const names = new Set<string>();
  for (const attr of holdingEntity?.attributes ?? []) {
    const group = attr.configuration_data?.registration_group;
    if (typeof group === "string" && group) names.add(attr.code_name);
  }
  return names;
}

/**
 * Build read-only cards for HOLDING EAV attributes tagged with
 * `configuration_data.registration_group`, one card per group.
 *
 * This mirrors the registration wizard's grouped-panel rendering (see
 * `groupedHoldingPanelsNode` in new-registration-page.tsx) on the record
 * surface: attributes carrying the same `registration_group` render together
 * under that group's title, ordered by `order`, with `visible_when` applied
 * against the captured values so a conditional picker (e.g. a `<code>_product`
 * field gated on `used_<code>`) only appears when its trigger is set.
 *
 * Tenant-agnostic: the grouping key is config data on the attribute, never a
 * tenant slug. Any tenant that tags holding attributes with a
 * `registration_group` gets the matching record card for free.
 *
 * Template scoping: HOLDING-level product attributes project onto the shared
 * `holding` entity from a specific product template, carrying
 * `configuration_data.source_owner_uuid` = that template's uuid. A grouped
 * capture (e.g. OC's installed-component questions, defined on the "Roofing
 * Shingle" template) therefore appears in `holdingEntity.attributes` for the
 * whole org - but only belongs on holdings registered against that template.
 * We drop a template-projected grouped attribute whose `source_owner_uuid`
 * does not match the holding's own product template, so a Loosefill Insulation
 * registration no longer renders the roofing component questions. Attributes
 * with no template owner (or when the holding's template is unknown) are left
 * as-is. Generic - keyed on config data, never a tenant slug.
 */
export function buildGroupedHoldingCards({
  holding,
  holdingEntity,
}: GroupedHoldingCardOptions): GroupedHoldingCard[] {
  if (!holding || !holdingEntity) return [];

  const values: Record<string, unknown> = {};
  for (const attr of holding.attribute_values ?? []) {
    values[attr.code_name] = attr.value;
  }

  const holdingTemplateUuid = holding.product?.product_template?.uuid;

  const groups = new Map<string, EntityAttributeDefinition[]>();
  for (const attr of holdingEntity.attributes) {
    const group = attr.configuration_data?.registration_group;
    if (typeof group !== "string" || !group) continue;
    if (!attributeAppliesToHoldingTemplate(attr, holdingTemplateUuid)) continue;
    const bucket = groups.get(group) ?? [];
    bucket.push(attr);
    groups.set(group, bucket);
  }

  const cards: GroupedHoldingCard[] = [];
  for (const [title, attrs] of groups) {
    const ordered = [...attrs].sort((a, b) => a.order - b.order);
    const items: CardFieldItem[] = [];
    for (const attr of ordered) {
      const cd = attr.configuration_data as Record<string, unknown> | undefined;
      // A conditional picker (visible_when) only shows when its trigger is set,
      // exactly as the wizard folds it beneath the preceding Yes/No toggle.
      if (cd?.visible_when && !isFieldVisible(cd, values)) continue;
      // A Yes/No answer is always meaningful (both states carry information),
      // but any other captured value only appears when present - so a "Yes"
      // with no product identified shows just the Yes, not an empty picker row
      // ("the identified component product, if any").
      if (attr.field_type !== "BOOLEAN" && isDisplayEmpty(values[attr.code_name]))
        continue;
      items.push({
        field: defineField({
          code_name: attr.code_name,
          display_name: attr.display_name,
          field_type: attr.field_type as FieldType,
          configuration_data: attr.configuration_data,
          options: attr.configuration_data?.options,
        }),
        value: values[attr.code_name],
        readOnly: true,
      });
    }
    if (items.length > 0) cards.push({ title, items });
  }
  return cards;
}
